// WorkspaceExportTab - Export & Import Workspace ZIP
import { useState, useEffect, useCallback, useRef } from "react";
import { backupAPI, workspaceAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Upload, Trash2, Loader2, Package, CheckCircle, XCircle, Clock, RefreshCw, Layers, FileUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatRelativeTime } from "@/lib/utils";

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

export default function WorkspaceExportTab() {
  const [workspaces, setWorkspaces] = useState([]);
  const [exports, setExports] = useState([]);
  const [selectedWs, setSelectedWs] = useState("");
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importId, setImportId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteWsId, setDeleteWsId] = useState(null);
  const [deletingWs, setDeletingWs] = useState(false);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [wsRes, exRes] = await Promise.all([
        workspaceAPI.list().catch(() => ({ data: [] })),
        backupAPI.listWorkspaceExports().catch(() => ({ data: [] })),
      ]);
      setWorkspaces(wsRes.data || []);
      setExports(exRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadData();
    const iv = setInterval(loadData, 15000);
    return () => clearInterval(iv);
  }, [loadData]);

  const handleExport = async () => {
    if (!selectedWs) { toast.error("Sélectionnez un workspace"); return; }
    setExporting(true);
    try {
      await backupAPI.exportWorkspace(selectedWs);
      toast.success("Export lancé en arrière-plan");
      setTimeout(loadData, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setExporting(false); }
  };

  const handleDirectDownload = async () => {
    if (!selectedWs) { toast.error("Sélectionnez un workspace"); return; }
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const url = backupAPI.downloadWorkspaceUrl(selectedWs);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `workspace_export.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Téléchargement terminé");
    } catch (e) { toast.error("Erreur téléchargement"); }
    finally { setDownloading(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await backupAPI.deleteJob(deleteId);
      toast.success("Export supprimé");
      loadData();
    } catch (e) { toast.error("Erreur suppression"); }
    finally { setDeleting(false); setDeleteId(null); }
  };

  const handleImport = async () => {
    if (!importId) return;
    setImporting(true);
    try {
      await backupAPI.importWorkspace(importId);
      toast.success("Import lancé ! Un nouveau workspace sera créé.");
      setTimeout(loadData, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur import"); }
    finally { setImporting(false); setImportId(null); }
  };

  const handleDownloadExport = (jobId) => {
    const token = localStorage.getItem("token");
    const url = backupAPI.downloadUrl(jobId);
    window.open(`${url}?token=${token}`, "_blank");
  };

  const getStatusBadge = (status) => {
    const m = {
      completed: { label: "Terminé", icon: CheckCircle, color: "text-green-600" },
      running: { label: "En cours", icon: Loader2, color: "text-blue-600" },
      failed: { label: "Échoué", icon: XCircle, color: "text-red-600" },
    };
    const c = m[status] || { label: status, icon: Clock, color: "" };
    const Icon = c.icon;
    return (
      <Badge variant={status === "failed" ? "destructive" : status === "running" ? "secondary" : "default"} className={`gap-1 ${c.color}`}>
        <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
        {c.label}
      </Badge>
    );
  };

  const handleUploadZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".zip")) { toast.error("Le fichier doit être un .zip"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await backupAPI.uploadImportWorkspace(formData);
      toast.success("Import lancé depuis le fichier uploadé !");
      setTimeout(loadData, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'upload"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleDeleteWorkspace = async () => {
    if (!deleteWsId) return;
    setDeletingWs(true);
    try {
      await workspaceAPI.delete(deleteWsId);
      toast.success("Workspace supprimé avec succès");
      setSelectedWs("");
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la suppression"); }
    finally { setDeletingWs(false); setDeleteWsId(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* Action Card */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2"><Package className="h-5 w-5" /> Export Workspace</h3>
              <p className="text-emerald-100 text-sm mt-1">Exportez un workspace complet (cours, quiz, fichiers...)</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Select value={selectedWs} onValueChange={setSelectedWs}>
                <SelectTrigger className="w-full sm:w-[220px] bg-white/20 border-white/30 text-white">
                  <SelectValue placeholder="Choisir un workspace..." />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map(ws => (
                    <SelectItem key={ws.id} value={ws.id}>
                      <span className="flex items-center gap-2"><Layers className="h-4 w-4" />{ws.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleExport} disabled={exporting || !selectedWs} className="bg-white text-emerald-700 hover:bg-emerald-50 gap-2">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Exporter
              </Button>
              <Button variant="outline" onClick={handleDirectDownload} disabled={downloading || !selectedWs} className="border-white/30 text-white hover:bg-white/10 gap-2">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Direct
              </Button>
              <Button variant="outline" onClick={() => { if (selectedWs) setDeleteWsId(selectedWs); else toast.error("Sélectionnez un workspace"); }} disabled={!selectedWs} className="border-red-300/50 text-white hover:bg-red-500/20 gap-2">
                <Trash2 className="h-4 w-4" />
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Upload ZIP Import Card */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2"><FileUp className="h-5 w-5" /> Importer un workspace depuis un ZIP</h3>
              <p className="text-blue-100 text-sm mt-1">Uploadez un fichier ZIP exporté pour créer un nouveau workspace</p>
            </div>
            <div className="flex gap-2">
              <input type="file" accept=".zip" ref={fileInputRef} onChange={handleUploadZip} className="hidden" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-white text-blue-700 hover:bg-blue-50 gap-2">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                {uploading ? "Import en cours..." : "Choisir un fichier ZIP"}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Exports List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5" /> Exports Workspace</CardTitle>
          <Button variant="ghost" size="sm" onClick={loadData}><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          {exports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Aucun export workspace</p>
            </div>
          ) : (
            <div className="space-y-3">
              {exports.map(job => (
                <div key={job.id} className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getStatusBadge(job.status)}
                      {job.workspace_name && <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200"><Layers className="h-3 w-3 mr-1" />{job.workspace_name}</Badge>}
                    </div>
                    <p className="text-sm font-medium mt-1 truncate">{job.description || `Export workspace`}</p>
                    <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{job.started_at ? formatRelativeTime(job.started_at) : "-"}</span>
                      {job.size_bytes > 0 && <span>{formatBytes(job.size_bytes)}</span>}
                    </div>
                    {job.error && <p className="text-xs text-destructive mt-1 truncate">{job.error}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {job.status === "completed" && (
                      <>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownloadExport(job.id)} title="Télécharger"><Download className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => setImportId(job.id)} title="Importer"><Upload className="h-4 w-4" /></Button>
                      </>
                    )}
                    {job.status !== "running" && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteId(job.id)} title="Supprimer"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet export ?</AlertDialogTitle>
            <AlertDialogDescription>Le fichier sera définitivement supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <AlertDialog open={!!importId} onOpenChange={() => setImportId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Importer ce workspace ?</AlertDialogTitle>
            <AlertDialogDescription>Un nouveau workspace sera créé avec le suffixe "(importé)". Les données existantes ne seront pas affectées.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleImport} disabled={importing}>
              {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Importer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Workspace Dialog */}
      <AlertDialog open={!!deleteWsId} onOpenChange={() => setDeleteWsId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Supprimer ce workspace ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="text-red-600 font-semibold">ATTENTION :</span> Cette action est irréversible. Toutes les données du workspace ({workspaces.find(w => w.id === deleteWsId)?.name || ""}) seront définitivement supprimées : cours, quiz, fichiers, messages, etc.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteWorkspace} disabled={deletingWs} className="bg-red-600 hover:bg-red-700">
              {deletingWs && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
